/**
 * 1.4.1: 生成邀请链接弹窗组件
 * YUI 风格设计，紫色渐变头部
 * 支持设置有效期、最大使用次数
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Link, Loader2, Copy, Check, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createInvitationLink, type MemberRole } from '../services/organizationService';

interface InviteLinkModalProps {
  organizationId: string;
  onClose: () => void;
}

export function InviteLinkModal({
  organizationId,
  onClose,
}: InviteLinkModalProps) {
  const { t } = useTranslation();
  const [role, setRole] = useState<MemberRole>('member');
  const [validDays, setValidDays] = useState(7);
  const [maxUses, setMaxUses] = useState<string>('');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { result, error: apiError } = await createInvitationLink(
        organizationId,
        undefined, // 不限定邮箱
        role,
        validDays,
        maxUses ? parseInt(maxUses) : undefined
      );

      if (apiError) {
        setError(apiError.message);
        return;
      }

      if (result?.invite_token) {
        const link = `${window.location.origin}/invite/${result.invite_token}`;
        setGeneratedLink(link);
      }
    } catch (err: any) {
      setError(err.message || t('generateFailed', '生成失败'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (generatedLink) {
      try {
        await navigator.clipboard.writeText(generatedLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // 降级方案
        const textarea = document.createElement('textarea');
        textarea.value = generatedLink;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleReset = () => {
    setGeneratedLink(null);
    setError(null);
    setCopied(false);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* 背景遮罩 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
        />

        {/* 弹窗内容 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* 紫色渐变头部 */}
          <div className="bg-gradient-to-r from-primary to-purple-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Link className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">
                    {t('generateInviteLink', '生成邀请链接')}
                  </h2>
                  <p className="text-white/80 text-sm">
                    {t('generateInviteLinkDesc', '创建可分享的团队邀请链接')}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* 内容区域 */}
          <div className="p-6">
            {!generatedLink ? (
              /* 设置表单 */
              <div className="space-y-5">
                {/* 角色选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('memberRole', '成员角色')}
                  </label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as MemberRole)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                  >
                    <option value="member">Member - {t('normalMember', '普通成员')}</option>
                    <option value="owner">Owner - {t('administrator', '管理员')}</option>
                  </select>
                </div>

                {/* 有效期选择 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('validPeriod', '有效期')}
                  </label>
                  <select
                    value={validDays}
                    onChange={(e) => setValidDays(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none bg-white"
                  >
                    <option value={1}>1 {t('day', '天')}</option>
                    <option value={3}>3 {t('days', '天')}</option>
                    <option value={7}>7 {t('days', '天')}</option>
                    <option value={14}>14 {t('days', '天')}</option>
                    <option value={30}>30 {t('days', '天')}</option>
                  </select>
                </div>

                {/* 最大使用次数 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('maxUses', '最大使用次数')}
                    <span className="text-gray-400 font-normal ml-1">
                      ({t('optional', '选填')})
                    </span>
                  </label>
                  <input
                    type="number"
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    placeholder={t('unlimited', '不限制')}
                    min={1}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>

                {/* 错误提示 */}
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                {/* 生成按钮 */}
                <button
                  onClick={handleGenerate}
                  disabled={isLoading}
                  className="w-full px-6 py-3 text-white bg-gradient-to-r from-primary to-primary-dark rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('generating', '生成中...')}
                    </>
                  ) : (
                    <>
                      <Link className="w-4 h-4" />
                      {t('generateLink', '生成链接')}
                    </>
                  )}
                </button>
              </div>
            ) : (
              /* 生成结果 */
              <div className="space-y-5">
                {/* 链接显示区域 */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('inviteLink', '邀请链接')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={generatedLink}
                      readOnly
                      className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 truncate"
                    />
                    <button
                      onClick={handleCopy}
                      className={`px-4 py-2 rounded-lg transition-all flex items-center gap-1 ${
                        copied
                          ? 'bg-green-500 text-white'
                          : 'bg-primary text-white hover:bg-primary-dark'
                      }`}
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          {t('copied', '已复制')}
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          {t('copy', '复制')}
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* 提示信息 */}
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                  <p className="text-sm text-purple-700">
                    💡 {t('shareLinkHint', '将此链接发送给您想邀请的成员')}
                  </p>
                </div>

                {/* 重新生成按钮 */}
                <button
                  onClick={handleReset}
                  className="w-full px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('generateNewLink', '生成新链接')}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
